// §34.2 / §9.1 — Archive's registration stub.
//
// **Manifest and thunk only** (D-511). Workbench apps are lazy for the reason the Arcade's titles
// are (D-247): a utility registered as a constant puts its whole view, engine and CSS in the boot
// path, and D-507 measured what that costs. Nothing here may import the view — a plain `import`
// of `Archive.tsx` would pull it back into the entry chunk and nothing would look different.
//
// The chunk this thunk produces is named `lazy.Archive-<hash>.js` by `vite.config.ts`, which is
// what keeps it out of the service-worker precache by exact prefix (§34.16, D-199's rule).
import type { ArchiveShellAccess, InProcessApp } from '@hangar/shared';
import { createLazyApp } from '@/gamekit/lazy';
import { COPY } from '@/copy';
import { ARCHIVE_WINDOW } from '@/config';

export function createArchiveApp(access: ArchiveShellAccess): InProcessApp {
  return createLazyApp({
    app: {
      id: 'archive',
      name: COPY.archive.title,
      // §34.1 (D-505) — the wave's launcher home. `Utilities` already exists as a Store category
      // (Connections, M28); this is the first app to carry it on the registry side too.
      category: 'Utilities',
      icon: { glyph: 'archive', hue: 38 },
      keywords: ['zip', 'unzip', 'compress', 'extract', 'archive'],
      // §34.2 — Archive is the default opener for a zip. `viewer` rather than `editor`: it reads
      // an archive and writes files *out* of one, but it never edits an archive in place.
      fileTypes: [
        { ext: ['.zip'], mime: ['application/zip', 'application/x-zip-compressed'], role: 'viewer' },
      ],
      window: { ...ARCHIVE_WINDOW, multiWindow: true },
    },
    // The `import()` is written here on purpose: a thunk built anywhere else moves the split point.
    load: () => import('./Archive'),
    // §34.2 (D-509) — the D-059 capability, injected at composition. It never travels through
    // `mount`, so an external app can never obtain one.
    props: { access },
  });
}
